/**
 * IndexedDB Storage for Snapshots
 *
 * Provides async, non-blocking storage for workbook snapshots.
 * Uses IndexedDB instead of localStorage to avoid blocking the main thread.
 */

import { type DBSchema, type IDBPDatabase, openDB } from 'idb'

// ============================================================================
// Types
// ============================================================================

export interface CellFormat {
  backgroundColor: string | null
  fontColor: string | null
  fontBold: boolean
  fontItalic: boolean
  fontSize: number | null
  numberFormat: string | null
  horizontalAlignment: string | null
  verticalAlignment: string | null
  wrapText: boolean
}

export interface CellSnapshot {
  value: unknown
  formula: string | null
  format: CellFormat
}

export interface SheetSnapshot {
  id: string
  name: string
  cells: Record<string, CellSnapshot>
  usedRangeAddress: string
}

export interface Snapshot {
  id: string
  turnId: string | null
  timestamp: number
  sheets: SheetSnapshot[]
}

interface TurnSnapshotMapping {
  key: string // Format: "chatId:turnId" or "chatId:turnId:after"
  snapshotId: string
}

// ============================================================================
// Delta Types
// ============================================================================

export interface ToolDelta {
  toolCallId: string
  toolName: string
  timestamp: number
  /** Cells captured BEFORE the tool executed, keyed by "SheetName!A1" */
  beforeCells: Record<string, CellSnapshot>
  /** Ranges that were targeted for capture */
  affectedRanges: Array<{ sheetName: string | null; address: string }>
  /** How this delta was captured */
  strategy: 'targeted' | 'sheet-scan' | 'full-snapshot'
}

export interface TurnDeltaChain {
  /** Composite key: "chatId:turnId" */
  key: string
  turnId: string
  chatId: string
  deltas: ToolDelta[]
  /** False if any delta capture failed — system should fall back to full snapshot */
  isComplete: boolean
  timestamp: number
}

// ============================================================================
// Database Schema
// ============================================================================

interface SnapshotDBSchema extends DBSchema {
  snapshots: {
    key: string
    value: Snapshot
    indexes: { 'by-timestamp': number }
  }
  turnMappings: {
    key: string
    value: TurnSnapshotMapping
  }
  metadata: {
    key: string
    value: { snapshotIds: string[] }
  }
  deltaChains: {
    key: string
    value: TurnDeltaChain
    indexes: { 'by-timestamp': number }
  }
}

// ============================================================================
// Constants
// ============================================================================

const DB_NAME = 'workbook-snapshots'
const DB_VERSION = 2
const MAX_SNAPSHOTS = 20
const MAX_DELTA_CHAINS = 30
const SNAPSHOT_INDEX_KEY = 'snapshot-index'

// ============================================================================
// Database Instance
// ============================================================================

let dbPromise: Promise<IDBPDatabase<SnapshotDBSchema>> | null = null

/**
 * Get or create the database connection
 */
function getDB(): Promise<IDBPDatabase<SnapshotDBSchema>> {
  if (!dbPromise) {
    dbPromise = openDB<SnapshotDBSchema>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        // Snapshots store
        if (!db.objectStoreNames.contains('snapshots')) {
          const snapshotStore = db.createObjectStore('snapshots', {
            keyPath: 'id',
          })
          snapshotStore.createIndex('by-timestamp', 'timestamp')
        }

        // Turn-to-snapshot mappings
        if (!db.objectStoreNames.contains('turnMappings')) {
          db.createObjectStore('turnMappings', { keyPath: 'key' })
        }

        // Metadata (snapshot index order)
        if (!db.objectStoreNames.contains('metadata')) {
          db.createObjectStore('metadata')
        }

        // Delta chains store (v2)
        if (!db.objectStoreNames.contains('deltaChains')) {
          const deltaStore = db.createObjectStore('deltaChains', {
            keyPath: 'key',
          })
          deltaStore.createIndex('by-timestamp', 'timestamp')
        }
      },
    })
  }
  return dbPromise
}

// ============================================================================
// Snapshot Index Management
// ============================================================================

/**
 * Get the ordered list of snapshot IDs (oldest first)
 */
async function getSnapshotIndex(): Promise<string[]> {
  try {
    const db = await getDB()
    const metadata = await db.get('metadata', SNAPSHOT_INDEX_KEY)
    return metadata?.snapshotIds ?? []
  } catch {
    return []
  }
}

/**
 * Save the snapshot index
 */
async function saveSnapshotIndex(snapshotIds: string[]): Promise<void> {
  const db = await getDB()
  await db.put('metadata', { snapshotIds }, SNAPSHOT_INDEX_KEY)
}

/**
 * Delete the oldest snapshot
 * Returns true if a snapshot was deleted
 */
async function deleteOldestSnapshot(): Promise<boolean> {
  const index = await getSnapshotIndex()
  if (index.length === 0) {
    return false
  }

  const oldestId = index.shift()
  if (oldestId) {
    const db = await getDB()
    await db.delete('snapshots', oldestId)
    await saveSnapshotIndex(index)
    console.log(`[SnapshotDB] Deleted oldest snapshot: ${oldestId}`)
    return true
  }
  return false
}

// ============================================================================
// Snapshot Storage Functions
// ============================================================================

/**
 * Save a snapshot to IndexedDB
 * Automatically deletes oldest snapshots if we exceed MAX_SNAPSHOTS
 */
export async function saveSnapshot(snapshot: Snapshot): Promise<void> {
  // Enforce snapshot limit
  let index = await getSnapshotIndex()
  while (index.length >= MAX_SNAPSHOTS) {
    await deleteOldestSnapshot()
    index = await getSnapshotIndex()
  }

  const db = await getDB()
  await db.put('snapshots', snapshot)

  // Update index
  if (!index.includes(snapshot.id)) {
    index.push(snapshot.id)
    await saveSnapshotIndex(index)
  }

  // Estimate size for logging
  const sizeKB = (JSON.stringify(snapshot).length * 2) / 1024
  console.log(
    `[SnapshotDB] Saved snapshot ${snapshot.id} (${sizeKB.toFixed(1)}KB)`
  )
}

/**
 * Retrieve a snapshot by ID
 */
export async function getSnapshot(
  snapshotId: string
): Promise<Snapshot | null> {
  try {
    const db = await getDB()
    const snapshot = await db.get('snapshots', snapshotId)
    return snapshot ?? null
  } catch (error) {
    console.error(`[SnapshotDB] Failed to load snapshot ${snapshotId}`, error)
    return null
  }
}

/**
 * Delete a snapshot by ID
 */
export async function deleteSnapshot(snapshotId: string): Promise<void> {
  try {
    const db = await getDB()
    await db.delete('snapshots', snapshotId)

    // Update index
    const index = await getSnapshotIndex()
    const idx = index.indexOf(snapshotId)
    if (idx !== -1) {
      index.splice(idx, 1)
      await saveSnapshotIndex(index)
    }

    console.log(`[SnapshotDB] Deleted snapshot ${snapshotId}`)
  } catch (error) {
    console.error(`[SnapshotDB] Failed to delete snapshot ${snapshotId}`, error)
  }
}

/**
 * Get all snapshot IDs
 */
export async function getAllSnapshotIds(): Promise<string[]> {
  return getSnapshotIndex()
}

// ============================================================================
// Turn-Snapshot Association
// ============================================================================

/**
 * Associate a turn with a "before" snapshot
 */
export async function associateTurnWithSnapshot(
  turnId: string,
  chatId: string,
  snapshotId: string
): Promise<void> {
  try {
    const db = await getDB()
    const key = `${chatId}:${turnId}`
    await db.put('turnMappings', { key, snapshotId })
  } catch (error) {
    console.error('[SnapshotDB] Failed to associate turn with snapshot', error)
  }
}

/**
 * Get the snapshot ID for a turn
 */
export async function getSnapshotIdForTurn(
  turnId: string,
  chatId: string
): Promise<string | null> {
  try {
    const db = await getDB()
    const key = `${chatId}:${turnId}`
    const mapping = await db.get('turnMappings', key)
    return mapping?.snapshotId ?? null
  } catch {
    return null
  }
}

/**
 * Get a snapshot for a turn
 */
export async function getSnapshotForTurn(
  turnId: string,
  chatId: string
): Promise<Snapshot | null> {
  const snapshotId = await getSnapshotIdForTurn(turnId, chatId)
  if (!snapshotId) return null
  return getSnapshot(snapshotId)
}

/**
 * Associate a turn with an "after" snapshot
 */
export async function associateTurnWithAfterSnapshot(
  turnId: string,
  chatId: string,
  snapshotId: string
): Promise<void> {
  try {
    const db = await getDB()
    const key = `${chatId}:${turnId}:after`
    await db.put('turnMappings', { key, snapshotId })
  } catch (error) {
    console.error(
      '[SnapshotDB] Failed to associate turn with after snapshot',
      error
    )
  }
}

/**
 * Get the "after" snapshot ID for a turn
 */
export async function getAfterSnapshotIdForTurn(
  turnId: string,
  chatId: string
): Promise<string | null> {
  try {
    const db = await getDB()
    const key = `${chatId}:${turnId}:after`
    const mapping = await db.get('turnMappings', key)
    return mapping?.snapshotId ?? null
  } catch {
    return null
  }
}

// ============================================================================
// Debug Utilities
// ============================================================================

/**
 * Get storage usage info
 */
export async function getStorageInfo(): Promise<{
  snapshotCount: number
  snapshotIds: string[]
  totalSizeKB: number
}> {
  const db = await getDB()
  const index = await getSnapshotIndex()
  let totalSize = 0

  for (const id of index) {
    const snapshot = await db.get('snapshots', id)
    if (snapshot) {
      totalSize += JSON.stringify(snapshot).length * 2
    }
  }

  return {
    snapshotCount: index.length,
    snapshotIds: index,
    totalSizeKB: totalSize / 1024,
  }
}

/**
 * Clear all snapshots and delta chains
 */
export async function clearAllSnapshots(): Promise<void> {
  const db = await getDB()
  await db.clear('snapshots')
  await db.clear('turnMappings')
  await db.clear('deltaChains')
  await db.delete('metadata', SNAPSHOT_INDEX_KEY)
  console.log('[SnapshotDB] Cleared all snapshots and delta chains')
}

// ============================================================================
// Delta Chain Storage Functions
// ============================================================================

/**
 * Save or update a delta chain for a turn.
 * Automatically evicts oldest chains if exceeding MAX_DELTA_CHAINS.
 */
export async function saveDeltaChain(chain: TurnDeltaChain): Promise<void> {
  try {
    const db = await getDB()

    // Evict old chains if needed
    const allKeys = await db.getAllKeys('deltaChains')
    if (allKeys.length >= MAX_DELTA_CHAINS) {
      // Get all chains sorted by timestamp, delete oldest
      const allChains = await db.getAll('deltaChains')
      allChains.sort((a, b) => a.timestamp - b.timestamp)
      const toDelete = allChains.slice(
        0,
        allChains.length - MAX_DELTA_CHAINS + 1
      )
      for (const old of toDelete) {
        await db.delete('deltaChains', old.key)
      }
    }

    await db.put('deltaChains', chain)

    const cellCount = chain.deltas.reduce(
      (sum, d) => sum + Object.keys(d.beforeCells).length,
      0
    )
    console.log(
      `[SnapshotDB] Saved delta chain for turn ${chain.turnId} ` +
        `(${chain.deltas.length} deltas, ${cellCount} cells, complete=${chain.isComplete})`
    )
  } catch (error) {
    console.error('[SnapshotDB] Failed to save delta chain', error)
  }
}

/**
 * Get the delta chain for a turn.
 */
export async function getDeltaChain(
  turnId: string,
  chatId: string
): Promise<TurnDeltaChain | null> {
  try {
    const db = await getDB()
    const key = `${chatId}:${turnId}`
    const chain = await db.get('deltaChains', key)
    return chain ?? null
  } catch (error) {
    console.error('[SnapshotDB] Failed to get delta chain', error)
    return null
  }
}

/**
 * Delete a delta chain for a turn.
 */
export async function deleteDeltaChain(
  turnId: string,
  chatId: string
): Promise<void> {
  try {
    const db = await getDB()
    const key = `${chatId}:${turnId}`
    await db.delete('deltaChains', key)
  } catch (error) {
    console.error('[SnapshotDB] Failed to delete delta chain', error)
  }
}
